// Auth error taxonomy. Surfaced as translated messages; raw PocketBase
// text is never shown.
//
// F-6 (T-030): the previous version matched English substrings of the
// server prose ("exist", "already", "taken", "unique"). The prose
// changes between SDK releases; the field-level error codes are stable.
// We now match on `err.response.data.<field>.code` for validation
// errors and on `err.response.status` for HTTP-level errors.

export const AuthErrorCode = {
  WrongPassword: "wrong-password",
  EmailTaken: "email-taken",
  InvalidEmail: "invalid-email",
  WeakPassword: "weak-password",
  InvalidToken: "invalid-token",
  Offline: "offline",
  Unknown: "unknown",
} as const;

export type AuthErrorCode = (typeof AuthErrorCode)[keyof typeof AuthErrorCode];

export class AuthError extends Error {
  code: AuthErrorCode;
  constructor(code: AuthErrorCode, message?: string) {
    super(message ?? code);
    this.code = code;
    this.name = "AuthError";
  }
}

interface FieldError {
  code?: string;
  message?: string;
}
interface ResponseData {
  email?: FieldError;
  password?: FieldError;
  [field: string]: FieldError | undefined;
}

function getFieldError(err: unknown, field: string): FieldError | undefined {
  const data = (err as { response?: { data?: ResponseData } }).response?.data;
  if (!data) return undefined;
  return data[field];
}

function getStatus(err: unknown): number | undefined {
  return (
    (err as { status?: number }).status ??
    (err as { response?: { status?: number } }).response?.status
  );
}

function getMessage(err: unknown): string {
  return String((err as { message?: string })?.message ?? err).toLowerCase();
}

/** Map a thrown value (raw PocketBase error or DOMException) to a user code. */
export function classifyAuthError(err: unknown): AuthErrorCode {
  if (!err) return AuthErrorCode.Unknown;
  if (err instanceof AuthError) return err.code;
  // Offline path: navigator.onLine is the cheapest signal.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return AuthErrorCode.Offline;
  }

  const status = getStatus(err);
  const message = getMessage(err);

  if (status === 401) return AuthErrorCode.WrongPassword;

  // F-6: validation errors. The PocketBase SDK may or may not set an
  // HTTP status on the thrown error; we look for the structured
  // `response.data.<field>.code` first and only fall back to the
  // status code if no field error is present.
  const emailErr = getFieldError(err, "email");
  if (emailErr?.code === "validation_not_unique") {
    return AuthErrorCode.EmailTaken;
  }
  if (emailErr?.code === "validation_invalid_email") {
    return AuthErrorCode.InvalidEmail;
  }
  const pwdCode = getFieldError(err, "password")?.code ?? "";
  if (pwdCode.includes("length") || pwdCode.includes("weak")) {
    return AuthErrorCode.WeakPassword;
  }

  if (status === 422 || status === 400) {
    // No structured field error matched; do not match on prose (F-6).
    return AuthErrorCode.Unknown;
  }

  if (/failed to fetch|networkerror|network request failed|offline/.test(message)) {
    return AuthErrorCode.Offline;
  }
  // Suppress unused warning; the variable is kept for future status branches.
  void message;
  return AuthErrorCode.Unknown;
}

/** R-11: classify a `confirmPasswordReset` SDK error.
 *
 *  PocketBase answers with HTTP 400 + response.message
 *  `"Failed to authenticate (invalid or expired token)."` for an
 *  unknown / used / expired token, and HTTP 400 with a field-level
 *  error (`password.code` / `passwordConfirm.code`) for a client-side
 *  validation failure (mismatch or weak password). We do **not** have
 *  a separate "expired" code from the server — the API answers
 *  identically for "never existed" and "already used / past TTL" —
 *  so we collapse them into `invalid-token` and let the UI show the
 *  user a "request a new one" message. The off-band timing of an
 *  expired token is not worth a separate UI string. */
export function classifyResetError(err: unknown): AuthErrorCode {
  if (!err) return AuthErrorCode.Unknown;
  if (err instanceof AuthError) return err.code;
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return AuthErrorCode.Offline;
  }
  const status = getStatus(err);
  const message = getMessage(err);

  // Network failure: SDK bubbles these as plain TypeError("Failed to
  // fetch") under jsdom / offline. Surface them as `offline` so the
  // Reset page disables the form / shows the offline banner.
  if (/failed to fetch|networkerror|network request failed|offline/.test(message)) {
    return AuthErrorCode.Offline;
  }

  // Token problem: server says "Failed to authenticate (invalid or
  // expired token)." Detect by status alone so we don't depend on the
  // English message text (F-6 lesson). The SDK may put the error text
  // on the top-level message OR on `err.response.message`; check
  // both. Also catches the case where PocketBase answered 404 because
  // the token has been pruned.
  if (status === 400 || status === 404 || status === 403) {
    const respMsg = String(
      (err as { response?: { message?: string } })?.response?.message ?? "",
    ).toLowerCase();
    if (
      respMsg.includes("invalid or expired") ||
      respMsg.includes("invalid token") ||
      respMsg.includes("expired token")
    ) {
      return AuthErrorCode.InvalidToken;
    }
    // 400 with a field error on password / passwordConfirm → a
    // client-side validation failure (mismatch / weak). Caller maps
    // these to UI strings; we surface as `unknown` and let the page
    // show a generic message — the password-mismatch is caught
    // client-side before we ever call the SDK, so a real
    // server-side rejection here is unusual.
    const pwdErr = getFieldError(err, "password");
    const pwdConfirmErr = getFieldError(err, "passwordConfirm");
    if (pwdErr || pwdConfirmErr) return AuthErrorCode.Unknown;
    // Fallback for any other 4xx with a token-like shape: treat as
    // invalid-token so the user can request a fresh link.
    if (status === 400 || status === 404 || status === 403) {
      return AuthErrorCode.InvalidToken;
    }
  }
  return AuthErrorCode.Unknown;
}
